import {CDK_DRAG_CONFIG, CdkDrag, DragDropConfig} from '@angular/cdk/drag-drop';
import {Directive, ElementRef, HostListener, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ReorderableRowsDirective} from './reorderable-rows.directive';

// Inside the table so Bootstrap's `.table`-rooted cell rules keep matching the preview.
const dragConfig: DragDropConfig = {previewContainer: 'parent', previewClass: 'shadow'};

@Directive({
    selector: '[appReorderableRow]',
    standalone: true,
    hostDirectives: [CdkDrag],
    providers: [{provide: CDK_DRAG_CONFIG, useValue: dragConfig}],
})
export class ReorderableRowDirective {
    readonly rows = inject(ReorderableRowsDirective);

    private readonly drag = inject(CdkDrag);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    constructor() {
        this.drag.ended.pipe(takeUntilDestroyed()).subscribe(() => this.releaseCellWidths());
    }

    moveBy(event: Event, offset: number): void {
        event.preventDefault();
        this.rows.moveBy(this.drag, offset, event.target);
    }

    // The preview is a position: fixed clone, so its cells size to their content unless the widths are
    // written onto the cells before CDK clones the row.
    freezeCellWidths(): void {
        this.cells().forEach(cell => {
            cell.style.width = `${cell.getBoundingClientRect().width}px`;
        });
    }

    releaseCellWidths(): void {
        this.cells().forEach(cell => cell.style.removeProperty('width'));
    }

    @HostListener('keydown.alt.arrowUp', ['$event', '-1'])
    @HostListener('keydown.alt.arrowDown', ['$event', '1'])
    protected moveByRowShortcut(event: Event, offset: number): void {
        if (this.rows.keyboard() === 'row') {
            this.moveBy(event, offset);
        }
    }

    private cells(): HTMLTableCellElement[] {
        const row = this.host.nativeElement;
        return row instanceof HTMLTableRowElement ? Array.from(row.cells) : [];
    }
}
